'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import React, { useCallback, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { createSayHelloLead } from './actions';
import { type SayHelloFormData, sayHelloFormSchema } from './form';
import * as fpixel from '@/lib/analytics/fpixel';
import { getEventId } from '@/lib/analytics/capi';
import { getConsentData } from '@/components/cookie-modal';

/**
 * Utility component that lets customers contact us.
 */
export const SayHello: React.FC<{
  source: string;
  contentName?: string;
  isHealthRelated?: boolean;
}> = ({
  source,
  contentName,
  isHealthRelated = false
}) => {
  const t = useTranslations('lead-collector');
  const contactFormT = useTranslations('ContactForm');
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<SayHelloFormData>({
    resolver: zodResolver(sayHelloFormSchema),
    defaultValues: {
      privacyForCommercialUse: false
    }
  });
  const onSubmit = useCallback<SubmitHandler<SayHelloFormData>>(async data => {
    try {
      // Generate a unique event ID for deduplication
      const eventId = await getEventId();

      // Parse source to extract content type and ID
      const sourceParts = source.split('/');
      let contentType = '';
      let contentId = '';
      if (sourceParts.length >= 2) {
        contentType = sourceParts[0];
        contentId = sourceParts[1];
      }

      // Track client-side event if consent is given
      const consentData = getConsentData();
      if (consentData?.marketing && (!isHealthRelated || consentData.healthData) && window.fbq) {
        try {
          // Since only value seems to be getting through, let's use it more effectively
          const eventData = {
            content_type: contentType,
            content_id: contentId,
            // Use contentId as a numeric value if possible
            value: parseInt(contentId) || 0,
            // Still include these in case they start working
            content_name: contentName || contentId,
            name: contentName || contentId,
            title: contentName || contentId,
            department_name: contentName || contentId,
            currency: 'EUR' // Adding currency as it's often required with value
          };
          console.log('Sending Lead event with data:', eventData);
          window.fbq('track', 'Lead', eventData);
        } catch (error) {
          console.error('Error tracking Lead event:', error);
        }
      }

      // Create the lead in the database and track server-side event
      await createSayHelloLead({
        data,
        source,
        contentName,
        isHealthRelated,
        eventId
      });
      setDone(true);
    } catch (error) {
      console.error('Failed to submit form or track events:', error);
    }
  }, [source, contentName, isHealthRelated]);
  return <div className="bg-gray-100 py-12 lg:py-24" data-sentry-component="SayHello" data-sentry-source-file="component.tsx">
      <div className="container mx-auto px-8 ">
        <div className="md:flex items-end gap-x-4">
          <h3 className="">{contactFormT('title')}</h3>
          <p className=" text-gray-600 pb-3 text-sm">{contactFormT('subtitle')}</p>
        </div>

        {done ? <div className="text-left py-16">
            <h3 className="text-2xl  mb-4">{contactFormT('confirm-title')}</h3>
            <p className="text-gray-600">{contactFormT('confirm-subtitle')}</p>
          </div> : <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pt-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input {...register('name', {
            required: true
          })} type="text" placeholder={contactFormT('firstNamePlaceholder')} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black" />
              <input {...register('lastName', {
            required: true
          })} type="text" placeholder={contactFormT('lastNamePlaceholder')} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black" />

              <input {...register('email', {
            required: true,
            pattern: /^\S+@\S+$/i
          })} type="email" placeholder={contactFormT('emailPlaceholder')} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black" />

              <input {...register('phoneNumber', {
            required: true
          })} type="text" placeholder={contactFormT('phoneNumberPlaceholder')} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black" />

              <input {...register('city', {
            required: true
          })} type="text" placeholder={contactFormT('cityPlaceholder')} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black" />
            </div>

            <textarea {...register('message', {
          required: true
        })} placeholder={contactFormT('messagePlaceholder')} rows={4} className="w-full p-2 border-b border-gray-300 bg-transparent focus:outline-hidden focus:border-black resize-none" onInput={e => {
          const target = e.target as HTMLTextAreaElement;
          target.style.height = 'auto';
          target.style.height = `${target.scrollHeight}px`;
        }} ref={e => {
          const refCallback = register('message', {
            required: true
          }).ref;
          if (e && refCallback) {
            refCallback(e);
            // Set initial height
            e.style.height = 'auto';
            e.style.height = `${e.scrollHeight}px`;
          }
        }}></textarea>

            <div className="space-y-4">
              <div className="flex items-start md:items-center gap-x-2">
                <input type="checkbox" {...register('privacyForRegistration')} id="privacyForRegistration" />
                <label htmlFor="privacyForRegistration" className="text-sm text-gray-600">
                  {t('privacy-registration')} <span className="text-red-800">*</span>
                </label>
              </div>
              {errors.privacyForRegistration && <p className="text-red-900 text-xs mt-1">{errors.privacyForRegistration?.message}</p>}

              <div className="flex items-start md:items-center gap-2">
                <input type="checkbox" {...register('privacyForMedicalBooking')} id="privacyForMedicalBooking" />
                <label htmlFor="privacyForMedicalBooking" className="text-sm text-gray-600">
                  {t('privacy-medical')} <span className="text-red-800">*</span>
                </label>
              </div>
              {errors.privacyForMedicalBooking && <p className="text-red-900 text-xs mt-1">{errors.privacyForMedicalBooking?.message}</p>}

              <div className="flex items-start md:items-center gap-2">
                <input type="checkbox" {...register('privacyForCommercialUse')} id="privacyForCommercialUse" />
                <label htmlFor="privacyForCommercialUse" className="text-sm text-gray-600">
                  {t('privacy-commercial')}
                </label>
              </div>
            </div>

            <div className="flex justify-center md:justify-start">
              <button type="submit" className="bg-offblack text-sm font-medium text-white px-5 py-3 rounded-full hover:bg-offblue transition duration-300 w-full md:w-auto">
                {contactFormT('sendButton')}
              </button>
            </div>
          </form>}
      </div>
    </div>;
};